import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {useGetLink}  from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ArrowRight from "@insite/mobius/Icons/ArrowRight";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    icon = "icon",
    heading = "heading",
    variant = "variant",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    disableButton = "disableButton",
    openNewTab = "openNewTab",
}

interface Props extends WidgetProps {
    fields: {
        [fields.background]: "image";
        [fields.image]: string;
        [fields.disableButton]: boolean;
        [fields.heading]: string;
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.openNewTab]: boolean;
    };
}

export interface IconBoxStyles {
    wrapperStyles?: InjectableCss;
    imageWrapper?: InjectableCss;
    headingStyles?: InjectableCss;
    boxButton?: ButtonPresentationProps;
}

export const iconBoxStyles: IconBoxStyles = {
    wrapperStyles: {
        css: css`
            display: flex;
            background: ${getColor("primary.contrast")};
            margin: 10px;
            border: 1px solid black;
            flex-direction: column;
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            > div {
                width: 100%;
                height: 150px;
                img {
                    object-fit: contain;
                    object-position: center;
                    overflow: hidden;
                    height: 100%;
                }
            }
        `,
    },
    boxButton: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: center;
            letter-spacing: 1px;
            font-size: 12px;
            font-weight: 700;
            span {
                top: 0 !important;
                padding-left: 3px;
            }
        `,
    },

    headingStyles: {
        css: css`
            align-items: center;
            display: flex;
            flex-direction: column;
            height: 100%;
            h3 {
                margin: 1rem 0;
                border: 2px solid black;
            }
            > p {
                text-align: center;
                font-size: 18px;
                word-break: break-word;
                margin-bottom: 0px;
                margin: 0px 30px 0px 30px;
            }
        `,
    },
};

const styles = iconBoxStyles;

export const iconBox: React.FC<Props> = props => {
    const { url } = useGetLink(props.fields.buttonLink);
    const history = useHistory();
    const onClick = () => {
        if (!url) {
            return;
        }
        if (url.startsWith("http")) {
            window.open(url, "_blank");
        } else {
            props.fields.openNewTab ? window.open(url, "_blank") : history.push(url);
        }
    };

    return (
        <StyledWrapper className="tset" {...styles.wrapperStyles}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={props.fields.image}></LazyImage>
            </StyledWrapper>
            <StyledWrapper {...styles.headingStyles}>
                {parse(props.fields.heading, parserOptions)}
            </StyledWrapper>            
            <StyledWrapper>
            <Button {...styles.boxButton} variant={props.fields.variant} onClick={onClick}>
                        {props.fields.buttonLabel}
                        <ButtonIcon src={ArrowRight} size={20} />
                    </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: iconBox,
    definition: {
        group: "Basic",
        icon: "image",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                displayName: "Icon",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                name: fields.openNewTab,
                displayName: "Open in a new tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                fieldType: "General",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
